import { X } from "lucide-react";
import { useId, useRef, type ReactNode } from "react";

import { useDialog } from "../hooks/useDialog";

interface ModalProps {
  title: string;
  sub?: string;
  onClose: () => void;
  children: ReactNode;
  foot?: ReactNode;
  width?: number;
}

export function Modal({ title, sub, onClose, children, foot, width = 540 }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialog(panelRef, onClose);

  return (
    <div
      className="fixed inset-0 z-100 grid place-items-center bg-[rgba(5,8,14,0.6)] backdrop-blur-md animate-[hbc-fadein_0.16s_ease] in-data-[theme=light]:bg-[rgba(15,23,42,0.4)]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex flex-col max-w-[calc(100vw-40px)] max-h-[calc(100vh-60px)] overflow-hidden bg-bg-1 border border-border rounded-xl shadow-(--shadow-2) animate-[hbc-slideup_0.18s_cubic-bezier(0.2,0.7,0.2,1)]"
        style={{ width }}
      >
        <div className="flex items-center gap-3 px-5 pt-4 pb-3 border-b border-border-faint">
          <div>
            <h3 id={titleId} className="m-0 text-sm font-semibold">{title}</h3>
            {sub && <div className="text-[11.5px] text-text-muted">{sub}</div>}
          </div>
          <button
            className="ml-auto grid w-6.5 h-6.5 place-items-center rounded-md text-text-muted hover:bg-bg-hover hover:text-text"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex flex-col gap-3.5 px-5 py-4.5 overflow-y-auto">{children}</div>
        {foot && (
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-border-faint bg-bg-0">
            {foot}
          </div>
        )}
      </div>
    </div>
  );
}
